/**
 * Bots against ships, the landing zones (the coordinator, Oct 20: "an enemy landing a side's people saw (or a drone or tower
 * saw) is remembered as a mark with its age, decaying like the other marks (fair: only what was seen)"): with
 * `shipsDestructible`, once a second, each side's eyes — its people, its drones, its towers — look at the enemy's ships
 * low over a landing (coming down, down, lifting off) or parked on a pad; one seen leaves the place it lands at (the
 * flight's landing, or the pad) in `state.landingsSeen[team]` at that tick, a place already there (within
 * LANDING_INTEL.samePeople) taking the new tick. The team picture reads them fading with their age — a place's fade, slower
 * than a mover's mark (core/picture.ts landingZoneConfidence, teamLandingZones); a faded one is dropped here. The bot commander's raid waits at one for the next
 * flight (ai/efforts.ts). Pure; off, nothing.
 */
import type { GameState, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { parkedShips } from './dropship';
import { flightNow } from './shipNow';
import { shipsOn } from './shipDamage';
import { eyePointAt } from './los';
import { personLineOfSight } from './fineTerrain';
import { weatherSees } from './perception';
import { weatherEffectHere } from './weather';
import { personSetOf } from './scaleProfile';
import { dronePos, droneGroundDir } from './drones';
import { isTowerSensor, towerTeam, towerEyePoint } from './towers';
import { BELIEF_FLOOR, landingZoneConfidence } from './picture';
import { dacos } from './dmath';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';

export const LANDING_INTEL = {
  /** A ship this low over the ground (people) is landing or lifting: seen, its landing is a landing zone. */
  lowPeople: 15,
  /** A seen landing within this of one already remembered (people) is the same place. */
  samePeople: 30,
  /** The ship's body over the ground (people), and how far short of it a line to it ends (the ship stands in the line to its own middle). */
  bodyPeople: 2.5, nearPeople: 5,
  /** At most this many places a side, the freshest kept. */
  keep: 8,
};

/** The point on a line from `from` to the ship's body at `body` that ends short of the ship (`near` world units); `null` when `from` is beside it. */
function nearSide(from: Vec3, body: Vec3, near: number): Vec3 | null {
  const to = vecSub(from, body), L = vecLength(to);
  return L <= 2 * near ? null : vecAdd(body, vecScale(to, near / L));
}

/** Does one of `team`'s eyes — its people (by the weather's sight), its flying drones (inside their ground circle), its standing towers — see a ship whose body stands at `body` now? */
export function teamSeesShipAt(state: GameState, team: number, planetIndex: number, body: Vec3, tick: number): boolean {
  const planet = state.planets[planetIndex];
  if (!planet) return false;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, near = LANDING_INTEL.nearPeople * person, seconds = tick * PHYS_DT;
  const sees = (eye: Vec3) => { const at = nearSide(eye, body, near); return at === null || personLineOfSight(eye, at, state); };
  for (const u of state.players) {
    if (!u.alive || u.teamIndex !== team || u.planetIndex !== planetIndex || (u.landingLeft ?? 0) > 0 || u.boarded) continue;
    const eye = eyePointAt(planet, vecNormalize(u.surfaceDir), ps.losEyeHeight);
    const sight = weatherEffectHere(state, planetIndex, u.surfaceDir, seconds).sight * ps.k;
    if (vecLength(vecSub(body, eye)) > sight) continue;
    if (weatherSees(state, planetIndex, eye, body, seconds, sight) && sees(eye)) return true;
  }
  const ground = vecNormalize(vecSub(body, planet.position));
  for (const d of state.drones ?? []) {
    if (!d.alive || d.teamIndex !== team || d.planetIndex !== planetIndex) continue;
    if (dacos(Math.max(-1, Math.min(1, vecDot(droneGroundDir(d, planet.radius), ground)))) * planet.radius > ps.droneSightRadius) continue;
    if (sees(dronePos(d, state))) return true;
  }
  for (const s of state.structures) {
    if (!isTowerSensor(s) || s.planetIndex !== planetIndex || towerTeam(s, state.players) !== team) continue;
    if (sees(towerEyePoint(s, state))) return true;
  }
  return false;
}

/** Once a second (match.ts), with ships destructible: each side's landings seen, refreshed and faded. */
export function stepLandingIntel(state: GameState, tick: number): GameState {
  if (!shipsOn(state)) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  // the enemy's ships to be seen: each its side, its planet, the place it lands at, where its body is now
  const ships: Array<{ team: number; planetIndex: number; land: Vec3; body: Vec3; pad: boolean }> = [];
  for (const f of [...(state.dropships?.flights ?? [])].sort((a, b) => a.id - b.id)) {
    const pose = flightNow(state, f, tick)?.flight!.pose;   // 69.17 step 1: the ship as it is now
    if (!pose || pose.height > LANDING_INTEL.lowPeople * person) continue;
    const planet = state.planets[f.planetIndex];
    ships.push({ team: f.team, planetIndex: f.planetIndex, land: vecNormalize(f.land), body: vecAdd(pose.pos, vecScale(vecNormalize(vecSub(pose.pos, planet.position)), LANDING_INTEL.bodyPeople * person)), pad: false });
  }
  for (const p of parkedShips(state)) {
    const planet = state.planets[p.planetIndex];
    if (!planet) continue;
    ships.push({ team: p.team, planetIndex: p.planetIndex, land: vecNormalize(p.dir), body: eyePointAt(planet, vecNormalize(p.dir), LANDING_INTEL.bodyPeople * person), pad: true });
  }
  let changed = false;
  const out: NonNullable<GameState['landingsSeen']> = {};
  for (const team of teams) {
    const was = state.landingsSeen?.[team] ?? [];
    let list = was.filter(z => landingZoneConfidence(z.tick, tick) >= BELIEF_FLOOR);
    for (const sh of ships) {
      if (sh.team === team || !teamSeesShipAt(state, team, sh.planetIndex, sh.body, tick)) continue;
      const R = state.planets[sh.planetIndex].radius;
      const i = list.findIndex(z => z.planetIndex === sh.planetIndex && dacos(Math.max(-1, Math.min(1, vecDot(z.surfaceDir, sh.land)))) * R <= LANDING_INTEL.samePeople * person);
      const zone = { planetIndex: sh.planetIndex, surfaceDir: sh.land, tick, ...(sh.pad || (i >= 0 && list[i].pad) ? { pad: true as const } : {}) };
      list = i >= 0 ? list.map((z, k) => (k === i ? zone : z)) : [...list, zone];
    }
    list = [...list].sort((a, b) => b.tick - a.tick || a.surfaceDir.x - b.surfaceDir.x).slice(0, LANDING_INTEL.keep);
    if (list.length !== was.length || list.some((z, k) => z !== was[k])) changed = true;
    if (list.length) out[team] = list;
  }
  if (!changed) return state;
  if (Object.keys(out).length === 0) { const { landingsSeen: _gone, ...rest } = state; return rest as GameState; }
  return { ...state, landingsSeen: out };
}
